import { useCallback, useEffect, useMemo, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import { useAuth } from '@/hooks/useAuth';
import { useShell } from '@/hooks/useShell';

type FavoriteToggle = { item_id: string; is_favorite: boolean; saved_at: string | null };

/**
 * Canonical home favorites store: user_id + item_id.
 * Mirrors shell.favorite_ids and keeps FeedPanel / DigestPanel in sync.
 */
export function useFeedFavorites() {
  const { user } = useAuth();
  const { shell, reload } = useShell();
  const [localIds, setLocalIds] = useState<Set<string>>(() => new Set(shell?.favorite_ids ?? []));

  useEffect(() => {
    setLocalIds(new Set(shell?.favorite_ids ?? []));
  }, [shell?.favorite_ids?.join('|')]);

  const isFavorite = useCallback((itemId: string) => localIds.has(itemId), [localIds]);

  const toggleFavorite = useCallback(
    async (itemId: string) => {
      if (!user) return;
      const previous = new Set(localIds);
      const next = new Set(localIds);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      setLocalIds(next);
      try {
        const res = await apiRequest<FavoriteToggle>(`/shell/favorites/${itemId}`, {
          method: 'POST',
          channel: 'platform',
        });
        setLocalIds((current) => {
          const synced = new Set(current);
          if (res.is_favorite) synced.add(itemId);
          else synced.delete(itemId);
          return synced;
        });
        reload();
      } catch {
        setLocalIds(previous);
      }
    },
    [localIds, reload, user],
  );

  const canFavorite = Boolean(user);

  return useMemo(
    () => ({ isFavorite, toggleFavorite, canFavorite }),
    [canFavorite, isFavorite, toggleFavorite],
  );
}
